
import { defineComponent, Ref, ref, computed, inject } from "vue";
import { checkTranslations, hasTranslation } from "@/i18n";
import OwnCarTypeStep from "./OwnCarType.vue";
import KilometersStep from "./Kilometers.vue";
import CostsResult from "./Costs.vue";
import { OwnCarType } from "@/interfaces/OwnCarType";
import { CalculationResponse } from "@/interfaces/CalculationResponse";
import { calculate, mailCalculation } from "@/services/CostsCalculatorService";
import { GlobalConfig } from "@/interfaces/GlobalConfig";
import { MailCalculationRequest } from "@/interfaces/MailCalculationRequest";
import { ContentLoader } from "vue-content-loader";
import { event } from "vue-gtag";
import { TranslationResult } from "@/interfaces/TranslationResult";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "CostsCalculator",
  components: { OwnCarTypeStep, KilometersStep, CostsResult, ContentLoader },
  setup() {
    const wrapper: Ref<HTMLElement | null> = ref(null);
    const config = inject<GlobalConfig | undefined>("config");
    const translationResult: Ref<TranslationResult> = ref({
      loaded: false,
      error: false,
    });
    checkTranslations().then((result) => {
      translationResult.value = result;
    });

    const translation = useI18n();
    const error: Ref<boolean> = ref(false);
    const errorInPopup: Ref<boolean> = ref(false);
    const message: Ref<string> = ref("");
    const processing: Ref<boolean> = ref(false);
    const step: Ref<number> = ref(0);
    const totalSteps: Ref<number> = ref(2);
    const selectedOwnCarType: Ref<OwnCarType | undefined> = ref();
    const currentKilometers: Ref<number | undefined> = ref();
    const calculationResponse: Ref<CalculationResponse | undefined> = ref();
    const subscriptionCheaper: Ref<boolean> = ref(false);
    const currentStepValid = computed(() => {
      return !validateNext();
    });
    const showProgressBar = computed(() => {
      return !isHomeStep.value && !isCalculated.value;
    });
    const isHomeStep = computed(() => {
      return !isCalculated.value && step.value === 0;
    });
    const isStep1 = computed(() => {
      return !isCalculated.value && step.value === 1;
    });
    const isStep2 = computed(() => {
      return !isCalculated.value && step.value === 2;
    });
    const isCalculated = computed(() => {
      return calculationResponse.value !== undefined || processing.value === true;
    });
    const hasTip = computed(() => {
      if (isStep1.value) {
        return hasTranslation("own-car-type-tip-text");
      } else if (isStep2.value) {
        return hasTranslation("kilometers-tip-text");
      }
      return false;
    });
    const tipText = computed(() => {
      if (isStep1.value) {
        return translation.t("own-car-type-tip-text");
      } else if (isStep2.value) {
        return translation.t("kilometers-tip-text");
      }
      return "";
    });

    function setOwnCarType(ownCarType: OwnCarType) {
      selectedOwnCarType.value = ownCarType;
    }

    function setKilometers(kilometers: number) {
      currentKilometers.value = kilometers;
    }

    function resetCalculationFlow(resetStep = false) {
      if (resetStep) {
        step.value = 1;
      }
      calculationResponse.value = undefined;
      processing.value = false;
      triggerScrollToTop();
    }

    function navigatePrevious() {
      error.value = false;
      if (step.value === 2 && (calculationResponse.value || processing.value)) {
        event("back-to-step-2-costs-calculation-flow", {
          event_category: "calculation flow",
        });
        resetCalculationFlow();
      } else if (step.value >= 1) {
        event("back-to-step-1-costs-calculation-flow", {
          event_category: "calculation flow",
        });
        step.value--;
      }
      triggerScrollToTop();
    }

    function validateNext() {
      let isValid = true;
      switch (step.value) {
        case 1: {
          isValid = selectedOwnCarType.value !== null && selectedOwnCarType.value?.carSize !== undefined;
          break;
        }
        case 2: {
          isValid =
            currentKilometers.value !== undefined && currentKilometers.value > 0 && currentKilometers.value % 1 === 0;
          break;
        }
      }
      return isValid;
    }

    function navigateNext(clickEvent: Event) {
      clickEvent.preventDefault();
      if (validateNext()) {
        error.value = false;
        step.value++;
        // tracking
        switch (step.value) {
          case 1: {
            event("start-calculation-flow", {
              event_category: "calculation flow",
            });
            break;
          }
          case 2: {
            event("step-1-costs-calculation-flow", {
              event_category: "calculation flow",
              event_label:
                "carSize = " +
                selectedOwnCarType.value?.carSize +
                " / isElectric = " +
                selectedOwnCarType.value?.isElectric,
            });
          }
        }
        triggerScrollToTop();
      }
    }

    function calculateCosts() {
      event("step-2-costs-calculation-flow", {
        event_category: "calculation flow",
        event_label: "kilometers = " + currentKilometers.value,
      });
      //processing.value = true;
      calculate(
        selectedOwnCarType.value?.carSize || "",
        selectedOwnCarType.value?.isElectric || false,
        currentKilometers.value || 0,
        config ? config.locale : ""
      )
        .then((response) => {
          if (response.status === 200) {
            calculationResponse.value = response.data;
            subscriptionCheaper.value = response.data.CostResults.IsSubscriptionCheaperThanOwnCar;
            event("final-step-costs-calculation-flow", {
              event_category: "calculation flow",
              event_label:
                "isSubscriptionCheaperThanOwnCar = " + response.data.CostResults.IsSubscriptionCheaperThanOwnCar,
            });
          } else {
            error.value = true;
          }
          processing.value = false;
        })
        .catch((ex) => {
          error.value = true;
          if (ex?.response.status === 400) {
            if (currentKilometers.value) {
              if (isNaN(currentKilometers.value)) {
                message.value = translation.t("error-invalid-field-kilometers");
              } else if (!selectedOwnCarType.value?.carSize) {
                message.value = translation.t("error-required-field-carsize");
              } else {
                message.value = translation.t("error-no-calculation-found");
              }
            } else {
              message.value = translation.t("error-required-field-kilometers");
            }
          } else {
            message.value = ex.response.data;
          }
          processing.value = false;
        });
    }

    function subscribe() {
      if (config?.subscriptionUrl) {
        let params = "";
        if (subscriptionCheaper.value === false) {
          params = "?s=1";
        }
        event("redirect-to-subscribe", {
          event_category: "calculation flow",
          event_label: config?.subscriptionUrl + params,
        });
        window.location.href = config?.subscriptionUrl + params;
      }
    }

    function sendCalculationEmail() {
      processing.value = true;
      mailCalculation({
        carSize: selectedOwnCarType.value?.carSize,
        isElectric: selectedOwnCarType.value?.isElectric || false,
        kilometers: currentKilometers.value,
        locale: config ? config.locale : undefined,
      } as MailCalculationRequest)
        .then((response) => {
          if (response.status === 204) {
            // mail sent
          } else {
            error.value = true;
          }
          processing.value = false;
        })
        .catch(() => {
          error.value = true;
          processing.value = false;
        });
    }

    function scrolltoTop() {
      if (!wrapper.value) return;

      var scrolltoElement = wrapper.value as HTMLElement;

      const getOffsetTop = (element: HTMLElement) => {
        let offsetTop = 0;
        while (element) {
          offsetTop += element.offsetTop;
          element = element.offsetParent as HTMLElement;
        }
        return offsetTop;
      };

      if (scrolltoElement) {
        setTimeout(function () {
          var scrolltoPosition;

          // CHECK IF NAVIGATION EXIST
          var navBand = document.querySelector(".o-navigation") as HTMLElement;

          if (navBand) {
            var navBandHeight = navBand.offsetHeight;
            scrolltoPosition = getOffsetTop(scrolltoElement) - (navBandHeight + 30);
          } else {
            scrolltoPosition = getOffsetTop(scrolltoElement);
          }

          window.scroll({ top: scrolltoPosition, left: 0, behavior: "smooth" });
        }, 250);
      }
    }

    function triggerScrollToTop() {
      var viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      if (viewportWidth < 768) {
        scrolltoTop();
      }
    }

    return {
      step,
      totalSteps,
      selectedOwnCarType,
      currentKilometers,
      setOwnCarType,
      setKilometers,
      navigatePrevious,
      navigateNext,
      calculateCosts,
      sendCalculationEmail,
      subscribe,
      resetCalculationFlow,
      currentStepValid,
      showProgressBar,
      isHomeStep,
      isStep1,
      isStep2,
      isCalculated,
      hasTip,
      tipText,
      calculationResponse,
      translationResult,
      error,
      errorInPopup,
      message,
      processing,
      wrapper,
    };
  },
});
